import { PrescriptionHelper } from "@/Common/Helpers/PrescriptionHelper";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { PharmacyOrderMedicationStatus } from "@/Common/Models/UI/Enums";
import { MedicationHelper } from "@/Patient/Helpers/MedicationHelper";
import { MedicationsStatus } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { MedicalItem } from "./MedicalItem";
import { MedicalItemMedicationAndPrescriptionDosage } from "./MedicalItemMedicationAndPrescriptionDosage";
import { MedicalItemMedicationAndPrescriptionPrintHistory } from "./MedicalItemMedicationAndPrescriptionPrintHistory";
import { EnumPatientSummaryMedicationDisplayStatus } from "./MedicalItemMedicationDisplayStatus";

PrescriptionHelper; // force an import so it can be referenced in the `ExternalPrescriberDisplayName` JSDoc

class MedicalItemMedicationAndPrescription extends MedicalItem {
    public constructor(init?: Partial<MedicalItemMedicationAndPrescription>) {
        super();
        Object.assign(this, init);
    }

    // ViewModels Variables
    public PrescriptionTab_IsChecked: boolean;
    public PrescriptionTab_PendingStatus: string;
    public PrescriptionTab_IsEmptyRx: boolean;
    public PrescriptionTab_IsRenew: boolean;
    public isDSQMed_NotImported: boolean;
    public DSQRenew_MyleActiveMedicationIdsConflic: string[];
    public DSQRenew_MyleActiveMedicationIdsConflicToBeMarkedAsCompleted: string[];
    // ---------------------
    public OriginalMedicationId: string;

    public Name: string;
    public IsPast: boolean;
    public IsLongTerm: boolean;
    public Form: string;
    public IsSubstituable: boolean;
    public DIN: string;
    public IsPatientCompliant?: boolean;
    public Description: string;
    public DrugStrength: string;
    public StrengthUnit: string;
    public TotalDurationManual: number;
    public Route: string;
    public Instruction: string;
    public LegalStatus: string;
    public Indication: string;
    public TherapeuticIntention: string;
    public ProtocolId: string;
    public PickupDate: Date;
    public PaperCopyIsRequired: boolean;
    public TargetedFacilityName: string;
    public InitialRefillQuantity: string;
    public NumberOfRefill: number;
    public RefillQuantity: string;
    public RefillDuration: number;
    public RefillDispenseInterval: number;
    public RefillStartDate: Date;
    public MedicationNumber: number;
    public PrescriptionStatusInt: number;
    public PriorPrescriptionNumber: number;
    public CeaseDate: Date;
    public CeaseReason: string;
    public DiscontinueReasonLookupId: string;
    public CeasePrescriptionId: Guid;
    public CeaseEmployeeId: Guid;
    public CeaseEncounterId: string;
    public PatientInformed: boolean;
    public PharmacyInformed: boolean;
    public PrescribeDate: Date;
    public TreatmentTypeLookupId: string;
    public TreatmentTypeLookupValue: string;
    public InitialRefillQuantityTypeLookupId: string;
    public InitialRefillQuantityTypeLookupValue: string;
    public RefillQuantityTypeLookupId: string;
    public RefillQuantityTypeLookupValue: string;
    public PrescriptionStatusLookupId: string;
    public PrescriptionStatusLookupValue: string;
    public MDPracticianId: string;
    public PharmacistId: string;
    public DsqId: string;
    public DsqOIDVersion: string;
    public DsqEventId: string;
    public DsqSyncDateString: string;
    public SubstituableReason: string;
    public Ingredients: string;
    public IsDevice: boolean;
    public LastDSQSyncDate: Date;
    public LastDSQPickupDate: Date;
    public DSQNumberOfRefillRemaining: number;
    public PrescriptionId: string;
    public PrescriptionDate: Date;
    public EmployeeId: Guid;
    public CreatorEmployeeId: string;
    public SignDate: Date;
    public SummaryDisplayStatus: EnumPatientSummaryMedicationDisplayStatus;
    public TotalDurationManualDays: number;
    public RefillEndDate: Date;
    public TodayDate: Date;
    public WrittenDate: Date;
    public WrittenDatePrecision: LifeStageDatePrecisionEnum;
    public InternalNote: string;

    /**
     * The ID of the healthcare provider when selected from Contact Directory dropdown.
     */
    public ExternalPrescriberId: string;

    /**
     * The name of the external medication prescriber, or empty if not entered, or null if prescribed internally.
     *
     * See {@link PrescriptionHelper.IsPrescribedExternally} to check if this is an external prescriber.
     */
    public ExternalPrescriberDisplayName: string;

    public StartDatePrecision: LifeStageDatePrecisionEnum;

    public Dosages: Immutable.List<MedicalItemMedicationAndPrescriptionDosage>;
    public PrintHistory: readonly MedicalItemMedicationAndPrescriptionPrintHistory[];

    public CodeGen: string;
    public isPending: boolean;

    /**
     * Front-end only (not updatable).
     * It reflects the data from Rx Vigilance (Medications.Chronicity == "A").
     */
    public IsShortTerm: boolean;

    /**
     * Used only to display status of pharmacy order in frontend.
     */
    public pharmacyOrderMedicationStatus?: PharmacyOrderMedicationStatus;

    public static getSearchingMedicationName(medicationAndPrescriptionModel: MedicalItemMedicationAndPrescription): string {
        if (!medicationAndPrescriptionModel) {
            return null;
        }

        return MedicationHelper.getMedicationDinOrName(medicationAndPrescriptionModel.Name, medicationAndPrescriptionModel.DIN);
    }

    /**
     * Whether the medication is expired and long-term.
     */
    public readonly IsExpiredLongTermMed: boolean;

    /**
     * The medication status based on its prescription status, refill dates, and display settings. This is used for filtering and displaying medication based on user-selected filters.
     */
    public readonly VisibleForDisplayStatuses: MedicationsStatus;
}

export { MedicalItemMedicationAndPrescription };
